import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { MessageThread, ReplyBox } from '../../components/inquiries/MessageThread';
import { useInquiryCategories, categoryLabel, TYPE_LABELS, formatWhen } from '../../components/inquiries/inquiryShared';

const STATUS_COLORS = { new: 'badge-gold', contacted: 'badge-navy', completed: 'badge-green', declined: 'badge-red' };
const STATUS_LABELS = { new: 'New', contacted: 'In Progress', completed: 'Completed', declined: 'Declined' };
const VIEWS = [['', 'All'], ['unclaimed', 'Unclaimed'], ['mine', 'Mine']];

export default function AdminInquiries() {
  const categories = useInquiryCategories();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('id');
  const [me, setMe] = useState(null);
  const [staff, setStaff] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [view, setView] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/admin/auth/me').then(r => setMe(r.data)).catch(() => {});
    api.get('/inquiries/staff').then(r => setStaff(r.data)).catch(() => {});
  }, []);

  const loadList = useCallback(() =>
    api.get('/inquiries', { params: { view: view || undefined, status: status || undefined } }).then(r => setInquiries(r.data)).catch(() => {}),
  [view, status]);

  const loadDetail = useCallback((id, { keepNotes } = {}) =>
    api.get(`/inquiries/${id}`).then(r => {
      setDetail(r.data);
      if (!keepNotes) setNotes(r.data.inquiry.notes || '');
    }).catch(() => setDetail(null)),
  []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setMsg(''); if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId, loadDetail]);

  // Refresh when a notification arrives (fired by AdminShell) and every 20s as a fallback
  useEffect(() => {
    const refresh = () => { loadList(); if (selectedId) loadDetail(selectedId, { keepNotes: true }); };
    window.addEventListener('inquiries:changed', refresh);
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 20000);
    return () => { window.removeEventListener('inquiries:changed', refresh); clearInterval(t); };
  }, [loadList, loadDetail, selectedId]);

  const select = (id) => setParams(id ? { id: String(id) } : {});
  const isLead = ['owner', 'manager'].includes(me?.role);
  const staffName = (id) => staff.find(s => s.id === id)?.full_name.split(' ')[0];

  const act = async (fn, okMsg) => {
    setMsg('');
    try { await fn(); if (okMsg) setMsg(okMsg); }
    catch (e) { setMsg(e.response?.data?.error || 'Something went wrong'); }
    loadList(); if (selectedId) loadDetail(selectedId, { keepNotes: true });
  };

  const inq = detail?.inquiry;
  const mine = inq && me && inq.assigned_to === me.id;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Inquiries</h1>
          <p style={{ color: '#9a9a9a', fontSize: 13 }}>Sell, pawn, and general questions — routed by specialty, first to claim handles it</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {VIEWS.map(([val, label]) => (
              <button key={val} onClick={() => setView(val)}
                style={{ padding: '8px 14px', border: 'none', background: view === val ? '#1a2744' : 'transparent', color: view === val ? '#fff' : '#555', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{label}</button>
            ))}
          </div>
          <select className="form-control" style={{ width: 'auto' }} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Any status</option>
            {Object.entries(STATUS_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>
        {/* List */}
        <div>
          {inquiries.length === 0 ? <div className="empty-state"><p>No inquiries found</p></div> : inquiries.map(i => {
            const forMe = me && i.routed_to?.includes(me.id) && !i.assigned_to;
            return (
              <div key={i.id} className="card" onClick={() => select(i.id)}
                style={{ padding: '14px 18px', marginBottom: 10, cursor: 'pointer', border: String(i.id) === selectedId ? '2px solid #c9a227' : '2px solid transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ fontWeight: i.unread ? 800 : 600, color: '#1a2744', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {i.unread && <span title="Unread" style={{ width: 8, height: 8, borderRadius: '50%', background: '#c62828', display: 'inline-block' }} />}
                    {i.name}
                  </div>
                  <span className={`badge ${STATUS_COLORS[i.status] || 'badge-gray'}`} style={{ fontSize: 10 }}>{STATUS_LABELS[i.status] || i.status}</span>
                </div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span>{TYPE_LABELS[i.inquiry_type]}</span>
                  <span className="badge badge-gray" style={{ fontSize: 10 }}>{categoryLabel(categories, i.item_category)}</span>
                  {i.assigned_name
                    ? <span style={{ color: '#1a2744' }}>👤 {i.assigned_name.split(' ')[0]}</span>
                    : <span style={{ color: forMe ? '#c62828' : '#9a9a9a', fontWeight: forMe ? 700 : 400 }}>{forMe ? '⚡ Unclaimed — for you' : 'Unclaimed'}</span>}
                </div>
                <p style={{ fontSize: 13, color: '#444', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{i.item_description}</p>
                <div style={{ fontSize: 11, color: '#9a9a9a', marginTop: 6 }}>
                  {formatWhen(i.last_message_at || i.created_at)}
                  {i.message_count > 0 && <span> · 💬 {i.message_count}</span>}
                  {i.images?.length > 0 && <span> · 📷 {i.images.length}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail + thread */}
        {inq && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', position: 'sticky', top: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <h3 style={{ fontWeight: 800, color: '#1a2744' }}>{inq.name}</h3>
                <div style={{ fontSize: 12, color: '#9a9a9a' }}>{TYPE_LABELS[inq.inquiry_type]} · {categoryLabel(categories, inq.item_category)} · {formatWhen(inq.created_at)}</div>
              </div>
              <button onClick={() => select(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9a9a9a', alignSelf: 'flex-start' }}>✕</button>
            </div>

            {msg && <div className="alert alert-info" style={{ marginBottom: 12 }}>{msg}</div>}

            {/* Ownership */}
            <div style={{ background: inq.assigned_to ? '#f8f7f2' : '#fff3e0', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
              <div>
                {inq.assigned_to ? <>Handled by <strong>{mine ? 'you' : inq.assigned_name}</strong></> : <strong>Unclaimed</strong>}
                <div style={{ fontSize: 11, color: '#9a9a9a' }}>Routed to: {inq.routed_to.map(staffName).filter(Boolean).join(', ') || 'everyone'}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {!inq.assigned_to && <button className="btn btn-primary btn-sm" onClick={() => act(() => api.post(`/inquiries/${inq.id}/claim`), "It's yours.")}>✋ Claim</button>}
                {mine && !isLead && <button className="btn btn-ghost btn-sm" onClick={() => act(() => api.patch(`/inquiries/${inq.id}`, { assigned_to: null }))}>Release</button>}
                {isLead && (
                  <select className="form-control" style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }} value={inq.assigned_to || ''}
                    onChange={e => act(() => api.patch(`/inquiries/${inq.id}`, { assigned_to: e.target.value ? Number(e.target.value) : null }), 'Reassigned.')}>
                    <option value="">Unassigned</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                )}
              </div>
            </div>

            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.9, marginBottom: 12 }}>
              {inq.phone && <div><strong>Phone:</strong> <a href={`tel:${inq.phone}`} style={{ color: '#c9a227' }}>{inq.phone}</a></div>}
              {inq.email && <div><strong>Email:</strong> <a href={`mailto:${inq.email}`} style={{ color: '#c9a227' }}>{inq.email}</a></div>}
              {!inq.email && <div style={{ color: '#e65100', fontSize: 12 }}>⚠️ No email on file — the customer won't be notified of replies. Call or text them.</div>}
            </div>

            <div style={{ background: '#f8f7f2', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <p style={{ fontSize: 14, color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{inq.item_description}</p>
              {inq.images?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {inq.images.map(img => (
                    <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                      <img src={img.url} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #e8e8e8' }} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 12, paddingRight: 4 }}>
              <MessageThread messages={detail.messages} viewer="staff" emptyText="No messages yet. Your reply goes to the customer's conversation page." />
            </div>
            <ReplyBox placeholder={inq.email ? 'Reply to the customer (they get an email)… ⌘/Ctrl+Enter to send' : 'Reply to the customer…'} sendLabel="Send Reply"
              onSend={async ({ body, images }) => {
                await api.post(`/inquiries/${inq.id}/messages`, { body, images });
                loadDetail(inq.id, { keepNotes: true }); loadList();
              }} />
            <a href={inq.customer_link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#9a9a9a', display: 'inline-block', marginTop: 6 }}>View as customer ↗</a>

            <div style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 14 }}>
              <div className="form-group">
                <label className="form-label">Internal notes <span style={{ fontWeight: 400, color: '#9a9a9a' }}>(staff only)</span></label>
                <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Offer details, condition, etc." />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-sm" onClick={() => act(() => api.patch(`/inquiries/${inq.id}`, { notes }), 'Notes saved.')}>Save Notes</button>
                {['contacted', 'completed', 'declined'].filter(s => s !== inq.status).map(s => (
                  <button key={s} className={`btn btn-sm ${s === 'completed' ? 'btn-primary' : s === 'declined' ? 'btn-danger' : 'btn-outline'}`}
                    onClick={() => act(() => api.patch(`/inquiries/${inq.id}`, { status: s, notes }))}>
                    Mark {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
